import SetLoader from "@/components/sets/set-loader";
import { Button } from "@/components/ui/button";
import { useExperienceContext } from "@/context/experience.context";
import { useEffect, useState, useCallback, type FC } from "react";
import { Vector3 } from "three";

// Add TypeScript declarations for web audio API and custom properties
declare global {
  interface Window {
    webkitAudioContext: typeof AudioContext;
    _audioListenerAdded?: boolean; // Flag to track if audio event listeners have been added
    _audioContext?: AudioContext; // Shared audio context initialized on user gesture
    _audioBuffers?: { [key: string]: AudioBuffer }; // Pre-loaded audio buffers
  }
}
import { ViewerView } from "./viewer/ViewerView";
import { SceneIndicator } from "./viewer/SceneIndicator";
import { InteractionContext } from "./viewer/InteractionContext";
import { useTenantContext } from "@/context/tenant.context";
import { useBrandContext } from "@/context/brand.context";
import { ScenesDictionary } from "@/components/sets/set-dictionary";
import { VideoContext } from "./viewer/NB/VideoContext";
import { Menu } from "lucide-react";
import DesktopPage from "./Desktop";
import { Scene } from "@/models/scene";
import IntroVideo from "@/components/viewer/intro-video";
import WelcomePage from "./Welcome";
import ElevenLabsChatButton from "@/components/viewer/eleven-labs-chat-button";

const ViewerPage: FC = () => {
  const tenant = useTenantContext();
  const brand = useBrandContext();

  const [showChatButton, setShowChatButton] = useState(false);
  const [audioPlayedForSceneIds, setAudioPlayedForSceneIds] = useState<
    string[]
  >([]);
  const [currentAudioSource, setCurrentAudioSource] =
    useState<AudioBufferSourceNode | null>(null);

  // Play audio function that can be reused for different scenes
  const playAudio = useCallback(
    (audioFilePath: string, sceneId: string) => {
      if (currentAudioSource) {
        currentAudioSource.stop();
        setCurrentAudioSource(null);
      }

      if (audioPlayedForSceneIds.includes(sceneId)) {
        return; // Don't play if already played
      }

      // First try to use the pre-loaded audio buffer that was initialized during user interaction
      if (
        window._audioContext &&
        window._audioBuffers &&
        window._audioBuffers[sceneId]
      ) {
        console.log(`Using pre-loaded audio buffer for scene ${sceneId}`);

        // Resume the audio context (important for iOS Safari)
        if (window._audioContext.state === "suspended") {
          window._audioContext.resume();
        }

        // Create audio source from pre-loaded buffer
        const source = window._audioContext.createBufferSource();
        source.buffer = window._audioBuffers[sceneId];
        source.connect(window._audioContext.destination);
        source.start(0);
        setCurrentAudioSource(source);

        // Mark this scene's audio as played
        setAudioPlayedForSceneIds((prev) => [...prev, sceneId]);
        return;
      }

      console.log(
        `No pre-loaded buffer found for scene ${sceneId}, loading from scratch`
      );

      // Fallback to loading the audio file directly
      // This approach likely won't work on iOS Safari without direct user interaction
      try {
        // Using Web Audio API for better cross-browser compatibility
        const audioContext = new (window.AudioContext ||
          window.webkitAudioContext)();

        // Resume the audio context (important for iOS Safari)
        if (audioContext.state === "suspended") {
          audioContext.resume();
        }

        // Fetch the audio file
        fetch(audioFilePath)
          .then((response) => response.arrayBuffer())
          .then((arrayBuffer) => audioContext.decodeAudioData(arrayBuffer))
          .then((audioBuffer) => {
            // Create audio source
            const source = audioContext.createBufferSource();
            source.buffer = audioBuffer;
            source.connect(audioContext.destination);
            source.start(0);
            setCurrentAudioSource(source);

            // Mark this scene's audio as played
            setAudioPlayedForSceneIds((prev) => [...prev, sceneId]);
          })
          .catch((error) => {
            console.error(`Error playing audio ${audioFilePath}:`, error);
          });
      } catch (error) {
        console.error(`Failed to initialize audio context: ${error}`);
      }
    },
    [
      currentAudioSource,
      audioPlayedForSceneIds,
      setCurrentAudioSource,
      setAudioPlayedForSceneIds,
    ]
  );

  const {
    setIsMenuDrawerOpen,
    handleExperienceVirtualObjectTap,
    currentScene,
    setCurrentScene,
    experienceVirtualObjects,
    experience,
    set,
    canViewExperience,
    isMobile,
  } = useExperienceContext();

  // State for the current camera position
  const [currentCameraPosition, setCurrentCameraPosition] =
    useState<THREE.Vector3>(new Vector3(1, 1, 4));

  // State for the current camera target position (only used intially)
  const [currentTargetPosition, setCurrentTargetPosition] =
    useState<THREE.Vector3>(new Vector3(0, 0, 0));

  const [currentlySelected, setCurrentlySelected] = useState<string | null>(
    null
  );

  /**
   * INTRO VIDEO STATE
   * State to keep track of whether the user has played the intro video or not
   * Uses local storage to persist the value across visits
   */
  const [hasPlayedIntroVideo, setHasPlayedIntroVideo] =
    useState<boolean>(false);
  useEffect(() => {
    if (hasPlayedIntroVideo) {
      localStorage.setItem("hasPlayedIntroVideo", "true");
    }
  }, [hasPlayedIntroVideo]);

  // NB specific states
  const [hasAcceptedPermissions, setHasAcceptedPermissions] =
    useState<boolean>(false);
  const [hasPlayedHostVideo, setHasPlayedHostVideo] = useState<boolean>(false);
  const [hasExperienceLoaded, setHasExperienceLoaded] =
    useState<boolean>(false);
  const [loadedVideo, setLoadedVideo] = useState<HTMLVideoElement | null>(null);
  const [loadedVideoTexture, setLoadedVideoTexture] =
    useState<THREE.VideoTexture | null>(null);

  const [navigatableScenes, setNavigatableScenes] = useState<Scene[]>([]);

  const handleSelection = (objId: string | null) => {
    setCurrentlySelected(objId);
  };

  const scenes = ScenesDictionary[set.setComponentName];

  useEffect(() => {
    setCurrentScene(scenes[0]);
    setNavigatableScenes(scenes.slice(1)); // remove the first scene from the list
  }, []);

  useEffect(() => {
    setCurrentCameraPosition(
      new Vector3(
        currentScene?.xCameraPosition,
        currentScene?.yCameraPosition,
        currentScene?.zCameraPosition
      )
    );

    setCurrentTargetPosition(
      new Vector3(
        currentScene?.xTargetPosition,
        currentScene?.yTargetPosition,
        currentScene?.zTargetPosition
      )
    );
  }, [currentScene]);

  // Scene specific states for chat button and audio.
  useEffect(() => {
    console.log("currentScene", currentScene);
    switch (currentScene?.id) {
      case "1":
        break;
      case "2":
        // Play audio for scene 2 (NB-Scene-2.mp3) with a 2-second delay
        if (!audioPlayedForSceneIds.includes("2")) {
          setTimeout(() => {
            // The pre-loaded audio buffer approach should work on iOS Safari
            // because we initialized the audio context during user interaction
            playAudio("/audio/NB-Scene-2.mp3", "2");
          }, 2000); // 2 second delay
        }
        break;
      case "3":
        // Play audio for scene 3 (NB-Scene-3.mp3) with a 1-second delay
        if (!audioPlayedForSceneIds.includes("3")) {
          setTimeout(() => {
            // The pre-loaded audio buffer approach should work on iOS Safari
            // because we initialized the audio context during user interaction
            playAudio("/audio/NB-Scene-3.mp3", "3");
          }, 1000); // 1 second delay
        }
        break;
      case "4":
        // Play audio for scene 4 (NB-Scene-4.mp3) with a 1-second delay
        if (!audioPlayedForSceneIds.includes("4")) {
          setTimeout(() => {
            // The pre-loaded audio buffer approach should work on iOS Safari
            // because we initialized the audio context during user interaction
            playAudio("/audio/NB-Scene-4.mp3", "4");
          }, 1000); // 1 second delay
        }
        setShowChatButton(true);
        break;
      case "5":
        break;
      case "6":
        break;
      default:
        break;
    }
  }, [currentScene, audioPlayedForSceneIds, playAudio]);

  const navigateNextScene = () => {
    const currentIndex = scenes.indexOf(currentScene);
    const nextIndex = (currentIndex + 1) % scenes.length;

    // prevent going back to the host video
    if (nextIndex === 0) {
      return -1;
    }

    handleSelection(null); // reset selection
    setCurrentScene(scenes[nextIndex]);
    return 0;
  };

  const canNavigateNextScene =
    (scenes.indexOf(currentScene) + 1) % scenes.length !== 0;

  const navigatePrevScene = () => {
    const currentIndex = scenes.indexOf(currentScene);
    const prevIndex = (currentIndex - 1 + scenes.length) % scenes.length;

    // prevent going back to the host video
    if (prevIndex === 0) {
      return -1;
    }

    handleSelection(null); // reset selection
    setCurrentScene(scenes[prevIndex]);
    return 0;
  };

  const canNavigatePrevScene =
    (scenes.indexOf(currentScene) - 1 + scenes.length) % scenes.length !== 0;

  // auto-transition from scene 1 to scene 2 after the host video has played
  useEffect(() => {
    if (hasPlayedHostVideo) {
      setCurrentScene(scenes[1]);
    }
  }, [hasPlayedHostVideo]);

  /**
   * Restrict access to the experience if the user is not allowed to view it
   */
  if (canViewExperience === false) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center py-16">
        <h1 className="mb-6 text-2xl font-bold  md:text-5xl">
          Experience coming soon...
        </h1>
      </div>
    );
  }

  /**
   * If the user is on desktop, show the desktop page
   */
  if (isMobile === false && experience.isMobileOnly === true) {
    return (
      <DesktopPage
        mobileOnlyExperienceImageUrl={experience.mobileOnlyDesktopImageUrl}
      />
    );
  }

  /**
   * Experience Viewer
   */

  if (experience.introVideoUrl && !hasPlayedIntroVideo) {
    {
      /* Show Intro Video if it exists and has not been played */
    }
    return (
      <IntroVideo
        introVideoUrl={experience.introVideoUrl}
        setHasPlayedIntroVideo={setHasPlayedIntroVideo}
      />
    );
  } else if (!hasAcceptedPermissions) {
    return (
      <WelcomePage
        hostVideoUrl={experience.hostVideoUrl}
        welcomeImageUrl={experience.welcomeImageUrl}
        setHasAcceptedPermissions={setHasAcceptedPermissions}
        setHasPlayedHostVideo={setHasPlayedHostVideo}
        setLoadedVideo={setLoadedVideo}
        setLoadedVideoTexture={setLoadedVideoTexture}
      />
    );
  } else {
    return (
      <div className="viewer-page h-full">
        {/* Top right button */}

        {showChatButton && (
          <div className="absolute top-4 right-4 z-50">
            <ElevenLabsChatButton />
          </div>
        )}

        {hasPlayedHostVideo && (
          <div className="h-full w-full z-[1] absolute p-10 flex flex-col justify-end gap-10 pointer-events-none">
            <div className="items-center justify-between flex flex-row">
              <Button
                className={`${
                  canNavigatePrevScene ? "" : "invisible"
                } pointer-events-auto`}
                onClick={(e) => {
                  e.stopPropagation();
                  navigatePrevScene();
                }}
              >
                {"❮ Prev"}
              </Button>
              <Button
                className="pointer-events-auto"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMenuDrawerOpen(true);
                }}
              >
                <Menu />
              </Button>
              <Button
                className={`${
                  canNavigateNextScene ? "" : "invisible"
                } pointer-events-auto`}
                onClick={(e) => {
                  e.stopPropagation();
                  navigateNextScene();
                }}
              >
                {"Next ❯"}
              </Button>
            </div>
            <SceneIndicator
              selectedSceneIndex={
                currentScene ? scenes.indexOf(currentScene) - 1 : 0
              }
              sceneCount={navigatableScenes.length}
            />
          </div>
        )}
        <SetLoader />
        <VideoContext.Provider
          value={{
            hasPlayedHostVideo,
            hasExperienceLoaded,
            video: loadedVideo,
            videoTexture: loadedVideoTexture,
          }}
        >
          <InteractionContext.Provider
            value={{
              currentSceneId: currentScene?.id,
              interactableProducts: experienceVirtualObjects
                .filter((e) => e.productId && e.sceneId === currentScene?.id)
                .map((e) => e.productId),
              currentlySelected,
              setCurrentlySelected: handleSelection,
            }}
          >
            <ViewerView
              set={set}
              currentScene={currentScene}
              scenes={scenes}
              currentCameraPosition={currentCameraPosition}
              currentTargetPosition={currentTargetPosition}
              experienceVirtualObjects={experienceVirtualObjects}
              experience={experience}
              setHasExperienceLoaded={setHasExperienceLoaded}
              handleExperienceVirtualObjectTap={
                handleExperienceVirtualObjectTap
              }
            />
          </InteractionContext.Provider>
        </VideoContext.Provider>
      </div>
    );
  }
};

export default ViewerPage;
